import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { services, industries } from "@/lib/data";
import ServicesGrid from "@/components/marketing/ServicesGrid";
import { FinalCTA } from "@/components/marketing/Sections";
import Breadcrumbs from "@/components/marketing/Breadcrumbs";
import SEO, { breadcrumbLd, serviceLd } from "@/components/SEO";
import { ArrowUpRight, Check } from "lucide-react";

const SERVICE_INDUSTRIES = {
    "new-business-technology-setup": ["restaurants-cafes", "medical-dental-clinics", "new-construction-ti", "hotels-hospitality"],
    "commercial-av-installation": ["hotels-hospitality", "retail-commercial", "restaurants-cafes"],
    "business-wifi-networking": ["restaurants-cafes", "hotels-hospitality", "medical-dental-clinics", "retail-commercial"],
    "digital-signage-menu-boards": ["restaurants-cafes", "retail-commercial", "hotels-hospitality"],
    "pos-workstation-coordination": ["restaurants-cafes", "retail-commercial"],
    "commercial-audio-systems": ["restaurants-cafes", "hotels-hospitality", "retail-commercial"],
    "cable-equipment-organization": ["property-managers-developers", "medical-dental-clinics", "retail-commercial"],
    "technology-troubleshooting-upgrades": ["restaurants-cafes", "medical-dental-clinics", "property-managers-developers"],
    "managed-support-plans": ["property-managers-developers", "hotels-hospitality", "medical-dental-clinics"],
};

export function ServicesIndex() {
    return (
        <MarketingLayout>
            <SEO
                title="Commercial Technology Services · Final Cut Technologies"
                description="Nine commercial technology service scopes — AV, business Wi-Fi, digital signage, POS coordination, cable management, audio, troubleshooting and managed support — across Metro Vancouver."
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Services", href: "/services" }]} />
                    <p className="eyebrow mb-6">Services</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Everything we install, organize and support.</h1>
                    <p className="mt-6 text-muted max-w-2xl leading-relaxed">Nine practical scopes for restaurants, hotels, clinics, retail, offices and mixed-use tenants — quoted after a walkthrough, not from a template.</p>
                </div>
            </section>
            <ServicesGrid />
            <FinalCTA />
        </MarketingLayout>
    );
}

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = services.find((s) => s.slug === slug);
    if (!service) return <Navigate to="/services" replace />;

    const relatedServices = services.filter((s) => s.slug !== slug).slice(0, 3);
    const relatedIndustries = (SERVICE_INDUSTRIES[slug] || []).map((id) => industries.find((i) => i.slug === id)).filter(Boolean);
    const url = typeof window !== "undefined" ? window.location.href : `/services/${slug}`;

    return (
        <MarketingLayout>
            <SEO
                title={`${service.title} in Metro Vancouver · Final Cut Technologies`}
                description={`${service.short} Serving Vancouver, Burnaby, Richmond, Surrey and the Lower Mainland.`}
                jsonLd={[
                    serviceLd(service.title, service.lead, url),
                    breadcrumbLd([
                        { name: "Home", href: "/" },
                        { name: "Services", href: "/services" },
                        { name: service.title, href: `/services/${slug}` },
                    ]),
                ]}
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Services", href: "/services" }, { name: service.title, href: `/services/${slug}` }]} />
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-7">
                            <p className="eyebrow mb-6">Service · {service.number}</p>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                                className="font-display text-5xl md:text-[80px] tracking-tight leading-[0.9]"
                            >
                                {service.title}
                            </motion.h1>
                            <p className="mt-8 text-lg text-ink/85 max-w-xl leading-relaxed">{service.lead}</p>
                            <div className="mt-10 flex flex-wrap gap-3">
                                <Link to="/contact" className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors duration-300">Get a Project Estimate <ArrowUpRight className="w-4 h-4" /></Link>
                                <Link to="/services" className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-4 text-sm hover:bg-ink hover:text-bg transition-colors duration-300">All services</Link>
                            </div>
                        </div>
                        <div className="md:col-span-5 relative aspect-[4/5] overflow-hidden bg-ink">
                            <img src={service.image} alt={`${service.title} — commercial installation reference in a Metro Vancouver environment`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute top-4 left-4 w-6 h-6 border-t border-l border-bg" />
                                <div className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-bg" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <p className="eyebrow mb-4">What's included</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">{service.summary}</h2>
                    </div>
                    <ul className="md:col-span-7 border-t border-line">
                        {service.deliverables.map((d, i) => (
                            <li key={d} className="flex items-start gap-4 py-5 border-b border-line">
                                <span className="font-mono text-xs text-muted w-8 pt-1">{String(i + 1).padStart(2, "0")}</span>
                                <Check className="w-5 h-5 mt-0.5 text-gold flex-none" />
                                <span className="text-ink-2">{d}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {service.note && (
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 pb-14 text-xs font-mono uppercase tracking-widest text-muted">
                        Note · {service.note}
                    </div>
                )}
            </section>

            {relatedIndustries.length > 0 && (
                <section className="border-b border-line bg-bg-alt">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                        <p className="eyebrow mb-6">Common for</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] mb-10 max-w-3xl">Industries that book this scope most.</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {relatedIndustries.map((ind) => (
                                <Link key={ind.slug} to={`/industries/${ind.slug}`} className="group border border-line bg-bg p-6 hover:bg-ink hover:text-bg transition-colors duration-300">
                                    <div className="flex items-center justify-between mb-8">
                                        <span className="font-mono text-xs tracking-widest text-muted group-hover:text-bg/70">Industry</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </div>
                                    <h3 className="font-display text-xl md:text-2xl tracking-tight mb-2">{ind.title}</h3>
                                    <p className="text-sm text-muted group-hover:text-bg/70">{ind.blurb}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="flex items-end justify-between gap-6 mb-10">
                        <div>
                            <p className="eyebrow mb-4">Related services</p>
                            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">Often booked alongside.</h2>
                        </div>
                        <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm link-underline">All services <ArrowUpRight className="w-4 h-4" /></Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {relatedServices.map((rs) => (
                            <Link key={rs.slug} to={`/services/${rs.slug}`} className="group border border-line p-6 hover:bg-ink hover:text-bg transition-colors duration-300">
                                <div className="flex items-center justify-between mb-8">
                                    <span className="font-mono text-xs tracking-widest text-muted group-hover:text-bg/70">{rs.number}</span>
                                    <ArrowUpRight className="w-4 h-4" />
                                </div>
                                <h3 className="font-display text-xl md:text-2xl tracking-tight mb-2">{rs.title}</h3>
                                <p className="text-sm text-muted group-hover:text-bg/70">{rs.short}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <FinalCTA />
        </MarketingLayout>
    );
}
